import { TinyColor } from '@ctrl/tinycolor'
import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function FramedLayout({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp, sp } = useOrientation(orientation)

  return (
    <AbsoluteFill className="p-[86px]">
      <div
        style={{ background: style.border.color }}
        className="flex h-full flex-col justify-between p-[86px]"
      >
        <div className="flex items-center space-x-[60px]">
          <FallbackImg
            className="aspect-square h-[194px] rounded-full"
            src={speaker?.avatarUrl}
          />

          <div className="w-full space-y-[29px]">
            <p
              style={style.speakerName}
              className="text-left text-[38px] leading-[120%]"
            >
              {speaker?.name}
            </p>

            <div className="w-full max-w-[500px]">
              <BarsVisualization
                color={style.waveform.color}
                gapSize={12}
                height={60}
                lineThickness={7}
                mediaStart={clipScene.mediaStart}
                mediaUrl={sceneAudioSrc(clipScene)}
                waveformUrl={clipScene.waveformUrl}
                mirror
                roundness={3}
              />
            </div>
          </div>

          {orientation === 'landscape' && (
            <div className="h-full">
              {clipScene.showZealousLogo && (
                <ZealousSvg
                  onlyIcon
                  color={style.logo.color}
                  className="w-[48px] shrink-0 opacity-50"
                />
              )}
            </div>
          )}
        </div>

        <div className="">{children}</div>

        <div
          className={cn(
            'flex shrink-0 items-center space-x-[42px] border-t-2 pt-[70px]',
            l && 'pt-[56px]',
          )}
          style={{
            borderColor: new TinyColor(style.logo.color)
              .setAlpha(0.5)
              .toRgbString(),
          }}
        >
          <FallbackImg
            className="aspect-square h-[104px] rounded-full object-cover"
            src={clipScene.imageUrl}
          />

          <div className="flex flex-col">
            <p
              className={cn(
                'line-clamp-1 w-full text-left text-[34px] leading-[150%]',
              )}
              style={style.title}
            >
              {clipScene.title}
            </p>

            <p
              className={cn(
                'line-clamp-1 w-full text-left text-[34px] leading-[150%]',
              )}
              style={style.subtitle}
            >
              {clipScene.subtitle}
            </p>
          </div>

          {orientation !== 'landscape' && clipScene.showZealousLogo && (
            <ZealousSvg
              onlyIcon
              color={style.logo.color}
              className="w-[48px] shrink-0 opacity-50"
            />
          )}
        </div>
      </div>
    </AbsoluteFill>
  )
}

export const framedLayoutInfo: LayoutInfo = {
  name: 'framed',
  Component: FramedLayout,
  defaultScene: {
    ...defaultClipScene,
    layout: 'framed',
  },
  defaultStyle: {
    background: { color: 'accent' },
    border: { color: 'primary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: { color: 'secondary', fontFamily: 'primary' },
    speakerName: { color: 'secondary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'secondary',
      fontFamily: 'primary',
      scrollType: 'line-by-line',
      highlightType: 'last-word',
      unPlayedColor: 'secondary',
      unPlayedOpacity: 0.5,
      fontSize: 58,
      fontWeight: 400,
      linesPerPage: 3,
      lineHeight: 1.5,
      textAlign: 'left',
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 60,
      lineHeight: 1.6,
      linesPerPage: 3,
    },
    portrait: {
      fontSize: 60,
      lineHeight: 1.8,
      linesPerPage: 9,
    },
  },
}
